import type { User } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { Cubit } from '../cubits/core'
import type { StaticModelCollection } from '../firestore-mobx/model'
import { getUserProfilePurchases } from '../firestore/UserProfilePurchase'
import { getUserProfileTokens } from '../firestore/UserProfileToken'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { UserProfilePurchase } from '../models/UserProfilePurchase'
import { UserProfileToken } from '../models/UserProfileToken'
import { fetchSlideDeck } from '../firestore/SlideDeck'
import type { SlideDeck } from '../models/SlideDeck'
import { UserProfileSectionPass } from '../models/UserProfileSectionPass'
import { getSectionPassesAndRebates } from '../firestore/UserProfileSectionPass'
import { DateTime } from 'luxon'
import { UserProfileSectionPassRebate } from '../models/UserProfileSectionPassRebate'
import { fetchSection } from '../firestore/Section'

export type ProfileTransaction =
  | {
      kind: 'assignmentPurchase'
      id: string
      date: Date | undefined
      token: UserProfileToken
      sectionId: string | undefined
    }
  | {
      kind: 'sectionPassPurchase'
      id: string
      date: Date | undefined
      sectionId: string
      grossPriceCents: number
      creditAppliedCents: number
      paidCents: number
    }
  | {
      kind: 'creditIssued'
      id: string
      date: Date | undefined
      amountCents: number
      rebatedFromSectionId: string | undefined
    }

export class UserProfileTokens extends Cubit {
  firestore: Firestore
  repository: FirebaseRepository
  user: BreakoutUser | User

  _tokensCollection: StaticModelCollection<UserProfileToken>
  _purchaseCollection: StaticModelCollection<UserProfilePurchase>
  _sectionPassCollection: StaticModelCollection<UserProfileSectionPass>
  _sectionPassRebateCollection: StaticModelCollection<UserProfileSectionPassRebate>

  @observable
  private _mappedSlideDecksToConsumedTokens:
    | Record<string, SlideDeck>
    | undefined = undefined

  // sectionId -> sectionName; null = fetch attempted but unreadable
  // (permission-denied for sections the user was removed from, or deleted)
  private _sectionNames = observable.map<string, string | null>()
  private _sectionNameFetchesInFlight = new Set<string>()

  @observable
  private _now = DateTime.now()

  private _updateNowTimeout: NodeJS.Timeout | undefined = undefined

  constructor(repository: FirebaseRepository, user: BreakoutUser | User) {
    super()
    makeObservable(this)
    this.user = user
    this.repository = repository
    this.firestore = repository.firestore
    this._tokensCollection = UserProfileToken.emptyCollection(repository)
    this._purchaseCollection = UserProfilePurchase.emptyCollection(repository)
    this._sectionPassCollection =
      UserProfileSectionPass.emptyCollection(repository)
    this._sectionPassRebateCollection =
      UserProfileSectionPassRebate.emptyCollection(repository)

    this.disposers.push(() => clearTimeout(this._updateNowTimeout))
  }

  startTokenStreamIfNotStarted() {
    const name = 'tokens'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getUserProfileTokens(this.repository, { userId: this.user.uid }),
      (tokens) => {
        // set date to now and clear timeout for the next tokensAvailable compute
        runInAction(() => (this._now = DateTime.now()))
        clearTimeout(this._updateNowTimeout)

        this._tokensCollection.replaceModels(tokens)
      },
      { name }
    )
  }

  startPurchasesStreamIfNotStarted() {
    const name = 'purchases'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getUserProfilePurchases(this.repository, { userId: this.user.uid }),
      (purchases) => {
        this._purchaseCollection.replaceModels(purchases)
      },
      { name }
    )
  }

  startSectionPassesStreamIfNotStarted() {
    const name = 'sectionPasses'
    // only start once
    if (this.hasStream(name)) return

    this.addStream(
      getSectionPassesAndRebates(this.repository, { userId: this.user.uid }),
      ({ rebates, passes }) => {
        this._sectionPassCollection.replaceModels(passes)
        this._sectionPassRebateCollection.replaceModels(rebates)
      },
      { name }
    )
  }

  async fetchConsumedTokenSlideDecks() {
    const consumedTokens = this.tokens.filter((token) => token.data.consumed)

    const slideDecksMappedToTokens: Record<string, SlideDeck> = {}

    await Promise.all(
      consumedTokens.map(async (token) => {
        if (!token.data.slideDeckId) return

        try {
          const deck = await fetchSlideDeck(this.repository, {
            slideDeckId: token.data.slideDeckId,
          })
          runInAction(() => (slideDecksMappedToTokens[token.id] = deck))
        } catch {
          // deck deleted or unreadable — the transactions UI renders a
          // generic fallback row instead of dropping the transaction
        }
      })
    )

    runInAction(
      () => (this._mappedSlideDecksToConsumedTokens = slideDecksMappedToTokens)
    )
  }

  @computed
  get mappedSlideDecksToConsumedTokens() {
    if (
      typeof this._mappedSlideDecksToConsumedTokens === 'undefined' &&
      this.tokensCollection.length > 0
    ) {
      // We can safely assume that the slide decks have not been fetched yet.
      this.fetchConsumedTokenSlideDecks()
    }

    return this._mappedSlideDecksToConsumedTokens
  }

  get tokensCollection() {
    this.startTokenStreamIfNotStarted()
    return this._tokensCollection
  }

  get purchaseCollection() {
    this.startPurchasesStreamIfNotStarted()
    return this._purchaseCollection
  }

  get sectionPassesCollection() {
    this.startSectionPassesStreamIfNotStarted()
    return this._sectionPassCollection
  }

  get sectionPassRebatesCollection() {
    this.startSectionPassesStreamIfNotStarted()
    return this._sectionPassRebateCollection
  }

  @computed
  get isLoading() {
    return (
      this.tokensCollection.isLoading ||
      this.purchaseCollection.isLoading ||
      this.sectionPassesCollection.isLoading
    )
  }

  @computed
  private get _availableAndExpiredTokens() {
    let lowestExpiresInMS = Number.POSITIVE_INFINITY

    const availableCount = this.tokens.reduce(
      (acc, token) => {
        const { eligible, expiresInMS } = token.isEligibleForConsumption(
          this._now
        )

        if (eligible && expiresInMS < lowestExpiresInMS)
          lowestExpiresInMS = expiresInMS

        if (eligible) {
          acc.available += token.data.tokenQuantity
        } else if (expiresInMS <= 0 && !token.data.consumed) {
          acc.expired += token.data.tokenQuantity
        }

        return acc
      },
      { available: 0, expired: 0 }
    )

    this.setUpdateNowTimeout(lowestExpiresInMS)

    return availableCount
  }

  @computed
  get availableTokens() {
    return this._availableAndExpiredTokens.available
  }

  @computed
  get expiredTokens() {
    return this._availableAndExpiredTokens.expired
  }

  @computed
  get consumedTokens() {
    return this.tokens
      .filter((token) => token.data.consumed === true)
      .reduce((acc, token) => acc + token.data.tokenQuantity, 0)
  }

  @computed
  private get _sectionPassRebateBalances() {
    this.startSectionPassesStreamIfNotStarted()
    return this._sectionPassRebateCollection.models.reduce(
      (acc, { data }) => {
        const { consumedAt, price } = data
        if (consumedAt) acc.consumed += price
        else acc.available += price
        return acc
      },
      { available: 0, consumed: 0 }
    )
  }

  /**
   * unified transaction history for the profile transactions tab,
   * sorted newest first:
   * - assignmentPurchase: consumed tokens, excluding tokens minted by a
   *   section pass (the pass itself is the transaction)
   * - sectionPassPurchase: section pass docs, with out-of-pocket price
   *   derived by subtracting rebates applied to that section
   * - creditIssued: every rebate doc; the trim-and-mint mechanics on the
   *   server keep issued amounts summing to the originally granted credit
   */
  @computed
  get transactions(): ProfileTransaction[] {
    const transactions: ProfileTransaction[] = []

    for (const token of this.tokens) {
      if (!token.data.consumed) continue
      if (token.data.purchaseType === 'sectionPass') continue
      transactions.push({
        kind: 'assignmentPurchase',
        id: token.id,
        date: token.data.purchaseDate ?? token.data.createdAt,
        token,
        sectionId: token.data.sectionId,
      })
    }

    const rebates = this.sectionPassRebatesCollection.models

    for (const pass of this.sectionPassesCollection.models) {
      // the pass doc id is the section id today, but prefer the explicit
      // field so this doesn't quietly break if that ever changes
      const sectionId = pass.data.sectionId ?? pass.id
      const grossPriceCents = pass.data.price ?? 0
      const creditAppliedCents = rebates.reduce((acc, rebate) => {
        if (rebate.data.rebateAppliedToSectionId !== sectionId) return acc
        return acc + rebate.data.price
      }, 0)
      transactions.push({
        kind: 'sectionPassPurchase',
        id: pass.id,
        date: pass.data.purchaseDate ?? pass.data.createdAt,
        sectionId,
        grossPriceCents,
        creditAppliedCents,
        paidCents: Math.max(0, grossPriceCents - creditAppliedCents),
      })
    }

    for (const rebate of rebates) {
      transactions.push({
        kind: 'creditIssued',
        id: rebate.id,
        date: rebate.data.purchaseDate ?? rebate.data.createdAt,
        amountCents: rebate.data.price,
        rebatedFromSectionId: rebate.data.rebatedFromSectionId,
      })
    }

    return transactions.sort(
      (a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0)
    )
  }

  @computed
  private get _transactionSectionIds() {
    const ids = new Set<string>()
    for (const transaction of this.transactions) {
      if (transaction.kind === 'creditIssued') {
        if (transaction.rebatedFromSectionId)
          ids.add(transaction.rebatedFromSectionId)
      } else if (transaction.sectionId) {
        ids.add(transaction.sectionId)
      }
    }
    return ids
  }

  /**
   * sectionId -> sectionName for every section referenced by a transaction;
   * null means the section is unreadable (user removed) or deleted and the
   * UI should fall back to a generic label. Lazily fetches missing entries,
   * following the mappedSlideDecksToConsumedTokens pattern.
   */
  @computed
  get transactionSectionNames() {
    this.fetchMissingSectionNames()
    return this._sectionNames
  }

  @computed
  get transactionSectionNamesLoading() {
    this.fetchMissingSectionNames()
    return [...this._transactionSectionIds].some(
      (id) => !this._sectionNames.has(id)
    )
  }

  private fetchMissingSectionNames() {
    const missing = [...this._transactionSectionIds].filter(
      (id) =>
        !this._sectionNames.has(id) && !this._sectionNameFetchesInFlight.has(id)
    )
    if (missing.length > 0) this.fetchSectionNames(missing)
  }

  private async fetchSectionNames(sectionIds: string[]) {
    sectionIds.forEach((id) => this._sectionNameFetchesInFlight.add(id))

    await Promise.all(
      sectionIds.map(async (sectionId) => {
        let name: string | null = null
        try {
          const section = await fetchSection(this.repository, { sectionId })
          if (!section.isEmpty) name = section.data.sectionName
        } catch {
          // permission-denied (user no longer in section) or missing doc;
          // the UI renders a generic label instead
        }
        runInAction(() => this._sectionNames.set(sectionId, name))
        this._sectionNameFetchesInFlight.delete(sectionId)
      })
    )
  }

  @computed
  get sectionPassRebateAvailableCents() {
    return this._sectionPassRebateBalances.available
  }

  @computed
  get sectionPassRebateConsumedCents() {
    return this._sectionPassRebateBalances.consumed
  }

  get tokens() {
    return this.tokensCollection.models
  }

  get purchases() {
    return this.purchaseCollection.models
  }

  get sectionPassSectionIds() {
    return new Set(this._sectionPassCollection.models.map((model) => model.id))
  }

  private setUpdateNowTimeout(expiresInMS: number) {
    clearTimeout(this._updateNowTimeout)

    // if no eligible tokens with expiry, no timeout is required
    if (!Number.isFinite(expiresInMS) || expiresInMS <= 0) return

    // clamp timeout to max supported values (max int32) if expiresInMS is too large
    const timeoutMs = Math.min(expiresInMS + 1, 2_147_483_647)

    // set a timeout to update the _now property, which will re-trigger tokensAvailable
    // and correctly set the token to to unavailable if expired
    this._updateNowTimeout = setTimeout(() => {
      runInAction(() => (this._now = DateTime.now()))
    }, timeoutMs) // add 1ms for safety
  }
}
